import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  MoreVertical,
  Music,
  Trash,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDeleteRecitalMutation } from '~entities/recital-manager/api/delete-recital';
import { useGetRecitalStats } from '~entities/recital-manager/api/get-recital-stats';
import type { Recital } from '~entities/recital-manager/model/recital';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { cn } from '~/shared/shadcn/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/shadcn/ui/alert-dialog';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import { formatDateIgnoreTimezone } from '~shared/utils/date-time';

import { RecitalVolunteersDialog } from './recital-volunteers-dialog';

interface RecitalCardProps {
  recital: Recital;
}

export const RecitalCard = ({ recital }: RecitalCardProps) => {
  const { hasAdminAccess } = useCurrentUserRole();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [volunteersDialogOpen, setVolunteersDialogOpen] = useState(false);
  const deleteRecitalMutation = useDeleteRecitalMutation();

  // Fetch recital stats
  const { data: statsData, isLoading: statsLoading } = useGetRecitalStats(
    recital._id,
  );

  // Get actual stats from API
  const totalPerformances = statsData?.totalPerformances || 0;
  const totalShows = statsData?.totalShows || 0;
  const hasConflicts = (statsData?.conflictsCount || 0) > 0;
  const totalRuntime = statsData?.totalDuration || 0; // Use actual total duration
  const missingMusic = 0; // TODO: Calculate actual missing music count

  const isReady = !hasConflicts && missingMusic === 0 && totalPerformances > 0;

  // Status badge styling
  const getStatusVariant = (
    status: string,
  ): 'default' | 'secondary' | 'outline' => {
    switch (status) {
      case 'published':
        return 'default';
      case 'archived':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Helper function to format duration
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const handleDelete = () => {
    deleteRecitalMutation.mutate(recital._id, {
      onSuccess: () => {
        toast.success('Recital deleted successfully!');
        setDeleteDialogOpen(false);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message || 'Failed to delete recital',
        );
      },
    });
  };

  return (
    <>
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border bg-gradient-to-br from-white via-white to-gray-50/30',
          'transition-all duration-200 hover:-translate-y-1 hover:shadow-lg',
          'h-full p-6',
        )}
      >
        {/* Header */}
        <div className='mb-4 flex items-start justify-between'>
          <Link
            to='/recitals/$recitalId'
            params={{ recitalId: recital._id }}
            className='group flex-1'
          >
            <h3 className='text-xl font-semibold capitalize text-gray-900 transition-colors group-hover:text-primary'>
              {recital.name}
            </h3>
            <div className='mt-1 flex items-center gap-2 text-sm text-muted-foreground'>
              <Calendar className='size-3.5' />
              <span>{recital.year}</span>
            </div>
          </Link>
          <div className='flex items-center gap-2'>
            <Badge variant={getStatusVariant(recital.status)}>
              {recital.status.toUpperCase()}
            </Badge>
            {hasAdminAccess && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Button variant='ghost' size='sm' className='size-8 p-0'>
                    <MoreVertical className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem asChild>
                    <Link
                      to='/recitals/$recitalId'
                      params={{ recitalId: recital._id }}
                    >
                      <Edit className='mr-2 size-4' />
                      View Details
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setVolunteersDialogOpen(true);
                    }}
                  >
                    <Users className='mr-2 size-4' />
                    Manage Volunteers
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                    className='text-red-600'
                  >
                    <Trash className='mr-2 size-4' />
                    Delete Recital
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Status Badges */}
        <Link
          to='/recitals/$recitalId'
          params={{ recitalId: recital._id }}
          className='block'
        >
          <div className='mb-4 flex flex-wrap gap-2'>
            {isReady && (
              <Badge
                variant='default'
                className='bg-green-500 hover:bg-green-600'
              >
                <CheckCircle className='mr-1 size-3' />
                Ready
              </Badge>
            )}
            {hasConflicts && (
              <Badge variant='destructive'>
                <AlertTriangle className='mr-1 size-3' />
                Conflicts
              </Badge>
            )}
            {missingMusic > 0 && (
              <Badge variant='secondary'>
                <Music className='mr-1 size-3' />
                Missing Music ({missingMusic})
              </Badge>
            )}
          </div>

          {/* Stats Grid */}
          <div className='mb-4 grid grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-gray-900'>
                {statsLoading ? '-' : totalPerformances}
              </div>
              <div className='text-xs text-muted-foreground'>Performances</div>
            </div>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-gray-900'>
                {statsLoading ? '-' : totalShows}
              </div>
              <div className='text-xs text-muted-foreground'>Shows</div>
            </div>
          </div>

          {/* Runtime */}
          <div className='flex items-center gap-2 border-t pt-4 text-sm text-muted-foreground'>
            <Clock className='size-4' />
            <span>
              Total Runtime:{' '}
              {statsLoading
                ? '-'
                : totalPerformances > 0
                  ? formatDuration(totalRuntime)
                  : 'No performances'}
            </span>
          </div>

          {/* Date Range */}
          <div className='mt-2 text-xs text-muted-foreground'>
            {formatDateIgnoreTimezone(recital.startDate)} -{' '}
            {formatDateIgnoreTimezone(recital.endDate)}
          </div>

          {/* Hover Effect Overlay */}
          <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100' />
        </Link>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recital</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{recital.name}"? This will also
              delete all associated shows and performances. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className='bg-red-600 hover:bg-red-700'
            >
              {deleteRecitalMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Volunteers Management Dialog */}
      <RecitalVolunteersDialog
        recitalId={recital._id}
        recitalName={recital.name}
        open={volunteersDialogOpen}
        onOpenChange={setVolunteersDialogOpen}
      />
    </>
  );
};
